import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider,
    CircularProgress,
    Checkbox,
    Radio,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useConfirm } from './ConfirmDialog';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import { API_URL } from '../api';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

/**
 * Renders the parent ↔ student link state for an account and provides
 * the three admin affordances:
 *   - List currently-linked counterpart(s)
 *   - Invite the missing half by email
 *   - Link to an existing account on the platform
 *   - Unlink
 *
 * Driven by the account's role: parents see their list of student
 * children; students see their (single) linked parent.
 */
const LinkedAccountsCard = ({ account, onChange }) => {
    const confirm = useConfirm();
    const [linked, setLinked] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [inviteOpen, setInviteOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);

    const isParent = account.role === 'parent';
    const isStudent = account.role === 'student';
    const otherRole = isParent ? 'student' : 'parent';
    const otherRoleLabel = isParent ? 'Student' : 'Parent';

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/accounts/${account._id}/linked`,
                authHeaders()
            );
            setLinked(data?.items || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load linked accounts.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [account._id]);

    if (!isParent && !isStudent) return null;

    const unlinkStudent = async (studentId) => {
        if (!(await confirm({ title: 'Unlink student', message: 'Unlink this student from the parent? Family emails will stop being cc\'d until you re-link.', confirmLabel: 'Unlink', danger: true }))) return;
        try {
            await axios.patch(`${API_URL}/api/admin/accounts/${studentId}`, { parentId: null }, authHeaders());
            setMessage('Unlinked.');
            await load();
            onChange?.();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to unlink.');
        }
    };

    const unlinkSelf = async () => {
        if (!(await confirm({ title: 'Unlink from parent', message: 'Unlink this student from their parent? Family emails will stop being cc\'d until you re-link.', confirmLabel: 'Unlink', danger: true }))) return;
        try {
            await axios.patch(`${API_URL}/api/admin/accounts/${account._id}`, { parentId: null }, authHeaders());
            setMessage('Unlinked.');
            await load();
            onChange?.();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to unlink.');
        }
    };

    return (
        <Card sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">
                    Linked {otherRoleLabel.toLowerCase()}{isParent ? 's' : ''}
                </Typography>
                <Stack direction="row" spacing={1}>
                    {(isParent || (isStudent && !account.parentId)) && (
                        <>
                            <Button
                                size="small"
                                startIcon={<LinkIcon />}
                                onClick={() => setLinkOpen(true)}
                            >
                                Link existing
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<PersonAddRoundedIcon />}
                                onClick={() => setInviteOpen(true)}
                            >
                                Invite {otherRoleLabel.toLowerCase()}
                            </Button>
                        </>
                    )}
                </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Linked accounts share the same <em>account status</em> (e.g. approving the parent also approves
                the linked students) and receive each other's transactional emails. Engagement is independent —
                it follows class enrolment per account.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            {loading ? (
                <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={20} /></Stack>
            ) : linked.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No linked {otherRoleLabel.toLowerCase()}
                    {isParent ? 's yet.' : ' yet.'}
                </Typography>
            ) : (
                <Stack divider={<Divider />}>
                    {linked.map((acc) => (
                        <Box key={acc._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {acc.firstName} {acc.surname}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {acc.email} · {acc.accountStatus} / {acc.engagementStatus}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                component={RouterLink}
                                to={`/${acc.role === 'parent' ? 'parents' : 'students'}/${acc._id}`}
                            >
                                Open
                            </Button>
                            <IconButton
                                size="small"
                                title="Unlink"
                                onClick={() => (isParent ? unlinkStudent(acc._id) : unlinkSelf())}
                            >
                                <LinkOffIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>
            )}

            {inviteOpen && (
                <InviteDialog
                    account={account}
                    otherRole={otherRole}
                    onClose={() => setInviteOpen(false)}
                    onSent={(email) => {
                        setMessage(`Invitation sent to ${email}.`);
                        setInviteOpen(false);
                    }}
                />
            )}
            {linkOpen && (
                <LinkExistingDialog
                    account={account}
                    otherRole={otherRole}
                    onClose={() => setLinkOpen(false)}
                    onLinked={() => {
                        setMessage('Linked.');
                        setLinkOpen(false);
                        load();
                        onChange?.();
                    }}
                />
            )}
        </Card>
    );
};

const InviteDialog = ({ account, otherRole, onClose, onSent }) => {
    const [form, setForm] = useState({ email: '', firstName: '', surname: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (!form.email.trim()) { setError('Email is required.'); return; }
        setBusy(true); setError('');
        try {
            await axios.post(
                `${API_URL}/api/admin/accounts/${account._id}/invite-link`,
                { inviteRole: otherRole, ...form },
                authHeaders()
            );
            onSent(form.email);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send invitation.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Invite a {otherRole}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                    <Typography variant="caption" color="text.secondary">
                        We'll email a registration link to the {otherRole}. When they accept, their new account is
                        automatically linked to <strong>{account.firstName} {account.surname}</strong>. They'll inherit
                        the same <em>account status</em> ({account.accountStatus}) and still need to verify their
                        email address.
                    </Typography>
                    <TextField
                        size="small" label="Email" fullWidth required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            size="small" label="First name (optional, prefill)" fullWidth
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                        />
                        <TextField
                            size="small" label="Surname (optional, prefill)" fullWidth
                            value={form.surname}
                            onChange={(e) => setForm({ ...form, surname: e.target.value })}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={busy} onClick={submit}>
                    {busy ? 'Sending…' : 'Send invitation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const LinkExistingDialog = ({ account, otherRole, onClose, onLinked }) => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState('');
    const [selected, setSelected] = useState(new Set());

    // Parents can link many students at once; a student has exactly one parent.
    const multiSelect = account.role === 'parent';
    const otherRoleLabel = otherRole === 'student' ? 'student' : 'parent';

    // Fetch every candidate of the target role up-front (capped) so admin can
    // tick straight from a full list without having to type first. Search
    // narrows the list client-side — cheap for the sizes this app handles.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError('');
            try {
                const { data } = await axios.get(
                    `${API_URL}/api/admin/accounts?role=${otherRole}&limit=500&sort=firstName`,
                    authHeaders()
                );
                if (cancelled) return;
                const list = (data?.items || []).filter((a) => String(a._id) !== String(account._id));
                setCandidates(list);
            } catch (err) {
                if (!cancelled) setError(err?.response?.data?.message || 'Failed to load candidates.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [account._id, otherRole]);

    const filtered = useMemo(() => {
        // Inlined so the useMemo dep list stays a clean literal.
        // Student who already has a parent can't be re-linked without
        // unlinking first; every parent is fair game for the "student
        // picks a parent" case.
        const ineligible = (c) => otherRole === 'student' && !!c.parentId;

        const needle = q.trim().toLowerCase();
        const matched = needle
            ? candidates.filter((c) => {
                const fullName = `${c.firstName || ''} ${c.surname || ''}`.toLowerCase();
                return fullName.includes(needle) || (c.email || '').toLowerCase().includes(needle);
            })
            : candidates;
        // Available candidates first, ineligible (already-linked) ones at
        // the bottom — otherwise the admin has to scroll past a wall of
        // disabled rows to find the one they can actually pick.
        return [...matched].sort((a, b) => Number(ineligible(a)) - Number(ineligible(b)));
    }, [candidates, q, otherRole]);

    // Same rule, exposed at component scope for the row-render disabled state.
    const isIneligible = (c) => otherRole === 'student' && !!c.parentId;

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(multiSelect ? prev : []);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const submit = async () => {
        if (selected.size === 0) return;
        setBusy(true); setError('');
        try {
            // Direction: writing parentId lives on the STUDENT side. If the
            // current account is a parent, PATCH each selected student to point
            // at us. If the current account is a student, PATCH self with the
            // single picked parent.
            if (account.role === 'parent') {
                for (const studentId of selected) {
                    // eslint-disable-next-line no-await-in-loop
                    await axios.patch(
                        `${API_URL}/api/admin/accounts/${studentId}`,
                        { parentId: account._id },
                        authHeaders()
                    );
                }
            } else {
                const [parentId] = Array.from(selected);
                await axios.patch(
                    `${API_URL}/api/admin/accounts/${account._id}`,
                    { parentId },
                    authHeaders()
                );
            }
            onLinked();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to link.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Link an existing {otherRoleLabel}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                    <Typography variant="caption" color="text.secondary">
                        Tick the {otherRoleLabel}
                        {multiSelect ? 's' : ''} you want to link to{' '}
                        <strong>{account.firstName} {account.surname}</strong>.
                        {multiSelect
                            ? ' You can pick more than one.'
                            : ' Only one parent can be linked at a time.'}
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder={`Filter by name or email`}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {loading ? (
                            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
                        ) : filtered.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                {candidates.length === 0
                                    ? `No ${otherRoleLabel} accounts on the platform yet.`
                                    : 'No matches for that filter.'}
                            </Typography>
                        ) : (
                            <Stack divider={<Divider />}>
                                {filtered.map((c) => {
                                    const disabled = isIneligible(c);
                                    const checked = selected.has(c._id);
                                    return (
                                        <Box
                                            key={c._id}
                                            onClick={() => !disabled && toggle(c._id)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 1,
                                                py: 0.75,
                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                opacity: disabled ? 0.55 : 1,
                                                '&:hover': disabled ? {} : { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            {multiSelect ? (
                                                <Checkbox
                                                    size="small"
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={() => toggle(c._id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <Radio
                                                    size="small"
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={() => toggle(c._id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            )}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                    {c.firstName} {c.surname}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                                    {c.email} · {c.accountStatus}
                                                    {disabled ? ' · already linked to another parent' : ''}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {selected.size > 0
                            ? `${selected.size} selected`
                            : 'Nothing selected yet.'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={submit}
                    disabled={busy || selected.size === 0}
                >
                    {busy
                        ? 'Linking…'
                        : multiSelect && selected.size > 1
                            ? `Link ${selected.size} students`
                            : `Link ${otherRoleLabel}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkedAccountsCard;
